import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  CalendarToday,
  ViewWeek,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
const requestConfig = { withCredentials: true };

export default function SchedulerEnhanced() {
  const [schedules, setSchedules] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'grid', 'week'
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    start_time: '',
    end_time: '',
    type: 'playlist',
    repeat_type: 'none',
    repeat_days: [],
    day_of_week: null,
    date: null,
    priority: 0,
    enabled: true,
  });

  useEffect(() => {
    fetchSchedules();
    fetchConflicts();
    fetchTemplates();
  }, []);

  const fetchSchedules = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/schedule`, requestConfig);
      setSchedules(res.data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };

  const fetchConflicts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/schedule/conflicts/list`, requestConfig);
      setConflicts(res.data);
    } catch (error) {
      console.error('Error fetching conflicts:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/schedule/templates/list`, requestConfig);
      setTemplates(res.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API_URL}/api/schedule`, formData, requestConfig);
      setOpenDialog(false);
      resetForm();
      fetchSchedules();
    } catch (error) {
      console.error('Error creating schedule:', error);
      alert('Error creating schedule: ' + error.message);
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API_URL}/api/schedule/${selectedSchedule.id}`, formData, requestConfig);
      setOpenDialog(false);
      setSelectedSchedule(null);
      resetForm();
      fetchSchedules();
    } catch (error) {
      console.error('Error updating schedule:', error);
      alert('Error updating schedule: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/schedule/${id}`, requestConfig);
      fetchSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      alert('Error deleting schedule: ' + error.message);
    }
  };

  const handleResolveConflict = async (conflictId) => {
    try {
      await axios.post(
        `${API_URL}/api/schedule/conflicts/${conflictId}/resolve`,
        {},
        requestConfig
      );
      fetchConflicts();
    } catch (error) {
      console.error('Error resolving conflict:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      start_time: '',
      end_time: '',
      type: 'playlist',
      repeat_type: 'none',
      repeat_days: [],
      day_of_week: null,
      date: null,
      priority: 0,
      enabled: true,
    });
    setSelectedSchedule(null);
  };

  const openEditDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setFormData({
      name: schedule.name || '',
      start_time: schedule.start_time || '',
      end_time: schedule.end_time || '',
      type: schedule.type || 'playlist',
      repeat_type: schedule.repeat_type || 'none',
      repeat_days: schedule.repeat_days || [],
      day_of_week: schedule.day_of_week,
      date: schedule.date || null,
      priority: schedule.priority || 0,
      enabled: schedule.enabled !== false,
    });
    setOpenDialog(true);
  };

  const getRepeatLabel = (schedule) => {
    if (schedule.repeat_type === 'daily') return 'Daily';
    if (schedule.repeat_type === 'weekly') {
      if (schedule.repeat_days && schedule.repeat_days.length > 0) {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return schedule.repeat_days.map(d => days[d]).join(', ');
      }
      return 'Weekly';
    }
    if (schedule.repeat_type === 'monthly') return 'Monthly';
    if (schedule.repeat_type === 'yearly') return 'Yearly';
    if (schedule.date) return 'One-time';
    if (schedule.day_of_week !== null) {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return days[schedule.day_of_week];
    }
    return 'Daily';
  };

  return (
    <Box>
      <PageHeader
        overline="Scheduling"
        title="Enhanced Schedule Manager"
        subtitle="Build advanced recurring schedules for your stations"
        icon={<ScheduleIcon />}
        actions={
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<ViewWeek />}
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            sx={{ color: '#22d3ee', borderColor: '#22d3ee' }}
          >
            {viewMode === 'grid' ? 'List View' : 'Grid View'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              resetForm();
              setOpenDialog(true);
            }}
            sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
          >
            New Schedule Entry
          </Button>
          </Box>
        }
      />

      {/* Conflicts Alert */}
      {conflicts.length > 0 && (
        <Card sx={{ bgcolor: '#ff6b6b20', border: '1px solid #ff6b6b', mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WarningIcon sx={{ color: '#ff6b6b' }} />
              <Typography variant="h6" sx={{ color: '#ff6b6b' }}>
                {conflicts.length} Schedule Conflict{conflicts.length > 1 ? 's' : ''} Detected
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              {conflicts.map((conflict) => (
                <Chip
                  key={conflict.id}
                  label={`${conflict.entry1_name} vs ${conflict.entry2_name}`}
                  onDelete={() => handleResolveConflict(conflict.id)}
                  sx={{ mr: 1 }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Schedules Table */}
      <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Repeat</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((schedule) => (
                  <TableRow key={schedule.id}>
                    <TableCell>{schedule.name}</TableCell>
                    <TableCell>
                      {schedule.start_time}
                      {schedule.end_time && ` - ${schedule.end_time}`}
                    </TableCell>
                    <TableCell>
                      <Chip label={schedule.type} size="small" />
                    </TableCell>
                    <TableCell>{getRepeatLabel(schedule)}</TableCell>
                    <TableCell>
                      {schedule.priority > 0 && (
                        <Chip label={schedule.priority} size="small" color="primary" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={schedule.enabled ? 'Enabled' : 'Disabled'}
                        color={schedule.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(schedule)}
                        sx={{ color: '#22d3ee' }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(schedule.id)}
                        sx={{ color: '#ff6b6b' }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedSchedule ? 'Edit Schedule' : 'Create New Schedule'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Schedule Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Time (optional)"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <MenuItem value="playlist">Playlist</MenuItem>
                  <MenuItem value="track">Track</MenuItem>
                  <MenuItem value="voicetrack">Voicetrack</MenuItem>
                  <MenuItem value="break">Break</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Repeat Type</InputLabel>
                <Select
                  value={formData.repeat_type}
                  onChange={(e) => setFormData({ ...formData, repeat_type: e.target.value })}
                >
                  <MenuItem value="none">None (One-time)</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.repeat_type === 'weekly' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Days of Week</InputLabel>
                  <Select
                    multiple
                    value={formData.repeat_days}
                    onChange={(e) => setFormData({ ...formData, repeat_days: e.target.value })}
                  >
                    <MenuItem value={0}>Sunday</MenuItem>
                    <MenuItem value={1}>Monday</MenuItem>
                    <MenuItem value={2}>Tuesday</MenuItem>
                    <MenuItem value={3}>Wednesday</MenuItem>
                    <MenuItem value={4}>Thursday</MenuItem>
                    <MenuItem value={5}>Friday</MenuItem>
                    <MenuItem value={6}>Saturday</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            {formData.repeat_type === 'none' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Specific Date (optional)"
                  type="date"
                  value={formData.date || ''}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Priority"
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.value })}
                >
                  <MenuItem value={true}>Enabled</MenuItem>
                  <MenuItem value={false}>Disabled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={selectedSchedule ? handleUpdate : handleCreate}
            variant="contained"
          >
            {selectedSchedule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
